import { useEffect, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { IOffices } from '../../types/Office';
import { ISigner } from '../../types/Parties';
import { RegionDetails } from '../constants/RegionDetails';
import { DocGenerationSource } from '../constants/docGenerationSource';
import { OrderAction } from '../constants/orderActions';
import { useAppDispatch, useAppSelector } from '../redux-store/hooks';
import { closeSnackbar } from '../redux-store/notifications/notificationsSlice';
import { setAlert } from '../redux-store/orders/ordersSlice';
import { RootState } from '../redux-store/store';
import { IOrderAction, ISettlementHistory } from '../type';
import { formatDate } from '../utils/helpers';
import { Status } from '../utils/notaryStatus';

const useOrderStates = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const orderDetailsRef = useRef<HTMLDivElement>();
  const documentRef = useRef<HTMLDivElement | null>(null);
  const form = useRef<HTMLFormElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const scrollToOrder = () => orderDetailsRef.current?.scrollIntoView();
  const scrollToDocument = () => documentRef.current?.scrollIntoView();
  const order: any = useAppSelector((state: RootState) => state.orders.order);
  const orders: any = useAppSelector((state: RootState) => state.orders);
  const isFetching: any = useAppSelector(
    (state: RootState) => state.orders.isFetching
  );
  const isFetchingText: any = useAppSelector(
    (state: RootState) => state.orders.isFetchingText
  );
  const alertUser: any = useAppSelector(
    (state: RootState) => state.orders.alertUser
  );
  const ipenSession: any = useAppSelector(
    (state: RootState) => state.ipenSession
  );
  const account: any = useAppSelector((state: RootState) => state.auth);
  const ipenNotary: any = useAppSelector(
    (state: RootState) => state.ipenNotary
  );
  const showAddCoSABtn: boolean = useAppSelector(
    (state: RootState) =>
      state.global.config.features.enableAddCoSettlementAgents
  );
  const currentUserEmail = account?.currentUser?.email?.toLowerCase();
  const currentUserName =
    account?.currentUser.given_name + ' ' + account?.currentUser.family_name;
  const isThirdPartyOrder = order?.OrderType?.DocGen === DocGenerationSource.CX;
  const notaryStatusOnOrder = order?.OrderDetails?.IPEN?.Notary?.Status;
  const havePendingNotary =
    order?.OrderDetails?.IPEN?.Notary !== null &&
    notaryStatusOnOrder === Status.Pending;
  const isOrderReviewedBySA: any =
    order?.Actions
      ?.PreClosing_SettlementAgent_ReviewOrderDetailsAndPartyInformation;
  const isDocumentReviewedBySA: any =
    order?.Actions?.PreClosing_SettlementAgent_ReviewDocumentPackage;
  const isOrderReviewedByNotary: any =
    order?.Actions?.PreClosing_Notary_ReviewOrderDetailsAndPartyInformation;
  const isDocumentReviewedByNotary: any =
    order?.Actions?.PreClosing_Notary_ReviewDocumentPackage;
  const isIdentifyWitnessPartyByNotary: any =
    order?.Actions?.OnClosing_Notary_IdentifyWitnessInformation ?? false;
  const notaryAssigned = order?.OrderDetails?.IPEN?.Notary !== null;
  const isCurrentUserNotaryOnOrder =
    order?.OrderDetails?.IPEN?.Notary?.Email?.toLowerCase() ===
    account.currentUser?.email?.toLowerCase();
  const closingDate = formatDate(
    order?.OrderDetails?.SigningDate,
    'ddd, MMM DD, YYYY'
  );
  const today = formatDate(new Date(), 'MM-DD-YYYY');
  const closingDateToCompare = formatDate(
    order?.OrderDetails?.SigningDate,
    'MM-DD-YYYY'
  );
  const preClosingActive = new Date(closingDateToCompare) > new Date(today);
  const onClosingActive = closingDateToCompare === today;
  const postClosingActive = new Date(closingDateToCompare) < new Date(today);
  const regionsList: IOffices['RegionOffices'] = RegionDetails;
  const notarySigned: boolean =
    order?.OrderDetails?.IPEN?.Notary?.SolexSigner?.SignerEvents.find(
      (el: { Name: string }) => el.Name?.toLowerCase().includes('sign')
    );
  const orderWitnesses = order?.OrderDetails?.Signers.filter(
    (signer: ISigner) => signer.SignerType == 'Witness'
  );
  const orderContainsWitnessSigners =
    orderWitnesses && orderWitnesses.length > 0;
  const orderNPE = order?.OrderDetails?.Signers.filter(
    (signer: ISigner) => signer.SignerType == 'NPE'
  );
  const orderContainsNPE = orderNPE && orderNPE.length > 0;
  const signerEvents: boolean =
    order?.OrderDetails?.IPEN?.Notary?.SolexSigner?.SignerEvents &&
    order?.OrderDetails?.IPEN?.Notary?.SolexSigner?.SignerEvents.length > 0;
  const orderStatus: string = order?.Status;
  const existingSettlementAgentsOnTheOrder =
    order?.OrderType?.DocGen === DocGenerationSource.FA
      ? order?.OrderDetails?.EscrowTeam?.map(
          (e: { Emails: any[] }) => e.Emails[0]
        )
      : order?.OrderDetails?.SettlementTeam?.map(
          (e: { Emails: any[] }) => e.Emails[0]
        );

  const [isCurrentUserEscrowOnOrder, setIsCurrentUserEscrowOnOrder] =
    useState<boolean>(false);
  const [
    isCurrentUserEscrowOfficerOnOrder,
    setIsCurrentUserOfficerEscrowOnOrder,
  ] = useState<boolean>(false);
  const [historiesToShow, setHistoriesToShow] = useState<ISettlementHistory[]>(
    []
  );

  useEffect(() => {
    const teamArray =
      order?.OrderType?.DocGen === DocGenerationSource.CX
        ? order?.OrderDetails?.SettlementTeam
        : order?.OrderDetails?.EscrowTeam;
    teamArray.forEach((sa: { Emails: string[]; Type: string }) => {
      if (
        sa.Emails.some(
          (email: string) => email.toLowerCase() === currentUserEmail
        )
      ) {
        setIsCurrentUserEscrowOnOrder(true);
        if (
          (order?.OrderType?.DocGen === DocGenerationSource.CX &&
            sa.Type === 'SettlementAgent') ||
          (order?.OrderType?.DocGen === DocGenerationSource.FA &&
            sa.Type === 'Escrow Officer')
        ) {
          setIsCurrentUserOfficerEscrowOnOrder(true);
        } else {
          setIsCurrentUserOfficerEscrowOnOrder(false);
        }
      }
    });
  }, [order]);

  useEffect(() => {
    return () => {
      dispatch(setAlert(null));
      dispatch(closeSnackbar());
    };
  }, [dispatch]);

  useEffect(() => {
    const existingActions: any = order?.Actions;
    if (existingActions) {
      const actionsToMap: Map<string, IOrderAction[]> = new Map(
        Object.entries(existingActions)
      );
      const historyArray: ISettlementHistory[] = [];

      actionsToMap.forEach((value, key) => {
        value.forEach((action: IOrderAction) => {
          const historyRecord: ISettlementHistory = {
            Name: getHybridOrderHistoryName(key),
            Signer: action?.Signer,
            TimeOccured: action?.TimeOccured,
          };
          historyArray.push(historyRecord);
        });
      });

      historyArray.sort((a: ISettlementHistory, b: ISettlementHistory) => {
        const firstDate = new Date(a.TimeOccured);
        const secondDate = new Date(b.TimeOccured);
        return secondDate.getTime() - firstDate.getTime();
      });

      setHistoriesToShow(historyArray);
    }
  }, [order]);

  const getHybridOrderHistoryName = (checklistId: string) => {
    switch (checklistId) {
      case OrderAction.PreClosing_SettlementAgent_ReviewOrderDetailsAndPartyInformation: {
        return 'Reviewed Order Details And Party Information';
      }
      case OrderAction.PreClosing_SettlementAgent_ReviewDocumentPackage: {
        return 'Reviewed Document Package';
      }
      case OrderAction.PreClosing_SettlementAgent_PrintAllInkSignDocuments: {
        return 'Printed All Ink Sign Documents';
      }
      case OrderAction.PreClosing_SettlementAgent_ConfirmFacilitateSigningInkSignDocuments: {
        return 'Confirmed Facilitate Signing Ink Sign Documents';
      }
      case OrderAction.PostClosing_SettlementAgent_UploadInkSignedDocumentsToVault: {
        return 'Uploaded Ink Signed Documents To Vault';
      }
      case OrderAction.PreClosing_SettlementAgent_UploadTagTitledocuments: {
        return 'Uploaded and Taged Title Documents';
      }
      case OrderAction.PostClosing_SettlementAgent_UploadAdditionaldocuments: {
        return 'Uploaded Additional Documents';
      }
      case OrderAction.Anytime_SettlementAgent_UploadAdditionalInkSigneddocuments: {
        return 'Uploaded Additional Ink Signed Documents';
      }
      case OrderAction.AnyTime_SettlementAgent_ResetPartyLogin: {
        return 'Reset Party Login';
      }
      case OrderAction.PreClosing_SettlementAgent_AddedCoSettlementAgent: {
        return 'Added Co-Settlement Agent';
      }
      case OrderAction.PreClosing_EscrowOfficer_AddedEscrowAssistant: {
        return 'Added Escrow Assistant';
      }
      case OrderAction.AnyTime_SettlementAgent_CanceledOrder: {
        return 'Canceled Order';
      }
      case OrderAction.PreClosing_SettlementAgent_ChangedNotary: {
        return 'Changed Notary';
      }
      case OrderAction.PreClosing_SettlementAgent_AssignedNotary: {
        return 'Assigned Notary';
      }
      case OrderAction.PreClosing_Notary_ReviewOrderDetailsAndPartyInformation: {
        return 'Notary Reviewed Order Details And Party Information';
      }
      case OrderAction.PreClosing_Notary_ReviewDocumentPackage: {
        return 'Notary Reviewed Document Package';
      }
      case OrderAction.OnClosing_Notary_IdentifiedWitnessParty: {
        return 'Notary Identified Witness Party';
      }
      case OrderAction.OnClosing_Notary_IdentifyWitnessInformation: {
        return 'Notary Identified Witness Information';
      }

      default:
        return '';
    }
  };

  return {
    params,
    orderDetailsRef,
    documentRef,
    scrollToOrder,
    scrollToDocument,
    form,
    input,
    order,
    navigate,
    dispatch,
    orders,
    isFetching,
    isFetchingText,
    alertUser,
    ipenSession,
    account,
    ipenNotary,
    showAddCoSABtn,
    isCurrentUserEscrowOnOrder,
    isCurrentUserEscrowOfficerOnOrder,
    existingSettlementAgentsOnTheOrder,
    isThirdPartyOrder,
    havePendingNotary,
    isOrderReviewedBySA,
    isDocumentReviewedBySA,
    isOrderReviewedByNotary,
    isDocumentReviewedByNotary,
    isIdentifyWitnessPartyByNotary,
    notaryAssigned,
    currentUserName,
    isCurrentUserNotaryOnOrder,
    closingDate,
    preClosingActive,
    onClosingActive,
    postClosingActive,
    regionsList,
    notarySigned,
    orderWitnesses,
    orderContainsWitnessSigners,
    orderContainsNPE,
    signerEvents,
    orderStatus,
    historiesToShow,
  };
};

export default useOrderStates;
